import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import forEach from 'lodash/forEach';

import { caseLogicMsg, dosageMsg, medicationMsg } from '../validationMessages';
import defaultRoutes from '../../config/routes';
import { unitTypes, dosageForms } from '../../config/medication';

function validateMedicationNodes(caseConf, nodeInfo, { isEffectChain, isMedicationEffectChain }) {
  const caseDosageForms = caseConf.dosageForms || [];
  const caseDosageFormsByMedicationId = keyBy(caseDosageForms, 'medicationId');
  const errors = [];
  const warnings = [];

  // Actions
  const medicationActionNodes = nodeInfo.actionNodes.filter(
    node => get(node, 'data.action.type') === 'MEDICATION'
  );

  medicationActionNodes.forEach(node => {
    if (isEffectChain) {
      return; // no actions in effect chains
    }
    const action = get(node, 'data.action');
    const medicationId = action.id;
    const caseDosageForm = caseDosageFormsByMedicationId[medicationId];

    if (!caseDosageForm) {
      errors.push(medicationMsg(`Medication "${medicationId}" is used in case logic but could not be found`, medicationId));
      errors.push(caseLogicMsg('Medication could not be found', node.id, { medicationId }));
    } else {
      if (!caseDosageForm.enabledRoutes.find(route => route.key !== 'IV_IO_INFUSION')) {
        errors.push(caseLogicMsg('Meidcation does not have any routes that can be administered', node.id, medicationId));
      }
      (action.routes || []).forEach((routeId) => {
        const route = caseDosageForm.enabledRoutes.find(r => r.key === routeId);

        if (!route) {
          const label = get(defaultRoutes.find(r => r.key === routeId), 'name.en', routeId);
          if (isMedicationEffectChain) {
            errors.push(caseLogicMsg(`Route "${label}" is used in effect chain but could not be found`, node.id, { medicationId, routeId }));
          } else {
            errors.push(dosageMsg(`Route "${label}" is used in case logic but could not be found`, caseDosageForm.id, { routeId }));
            errors.push(caseLogicMsg(`Route "${label}" could not be found`, node.id, medicationId, { routeId }));
          }
        }
        if (route && route.key === 'IV_IO_INFUSION') {
          errors.push(
            caseLogicMsg('Invalid route: IV/IO Infusion', node.id, { errorId: 'invalidRoute', routeId: 'IV_IO_INFUSION' })
          );
        }
      });
    }
  });


  // Checks
  nodeInfo.checkNodes.forEach((node) => {
    forEach(node.data, (check, checkKey) => {
      if (check.type === 'MEDICATION_ADMINISTERED') {
        const medicationId = check.id;
        const caseDosageForm = caseDosageFormsByMedicationId[medicationId];
        if (!caseDosageForm) {
          const medMsg = medicationMsg(`Medication "${medicationId}" is used in case logic but could not be found`, medicationId);
          const clMsg = caseLogicMsg('Medication could not be found', node.id, { errorId: 'medDisabled', medicationId, checkKey });
          if (isEffectChain) {
            // EC meds aren't required to be enabled, they are injected in SimCaseData at runtime
            // Case logic effect chains get warnings, default medication effect chains do not
            if (!isMedicationEffectChain) {
              const warningExists = warnings.find(w => w.id === medicationId);
              if (!warningExists) {
                // only one warning for each medication id
                warnings.push(medMsg);
                warnings.push(clMsg);
              }
            }
            return;
          } else {
            errors.push(medMsg);
            errors.push(clMsg);
          }
        } else {
          // Dosage units
          const unitsNeedValidation =
            check.dosage && (check.dosage.min || check.dosage.max) && check.routes.length === 1 && (check.target && check.target !== 'GIVEN');
          if (unitsNeedValidation) {
            if (!check.units) {
              errors.push(caseLogicMsg('Dosage unit is mandatory', node.id, { errorId: 'unitDisabled', medicationId, checkKey }));
            } else {
              const unitType = unitTypes.find(ut => ut.key === check.units);
              if (unitType && unitType.category) {
                const numeratorUnitType = unitTypes.find(ut => ut.key ===  caseDosageForm.concentration.numeratorUnits);
                const denominatorUnitType = unitTypes.find(ut => ut.key ===  caseDosageForm.concentration.denominatorUnits);
                if (unitType.category !== numeratorUnitType.category && (!denominatorUnitType || unitType.category !== denominatorUnitType.category)) {
                  errors.push(caseLogicMsg(
                    'Dosage unit not compatible with concentration', node.id, { errorId: 'dosageUnit', medicationId, checkKey }
                  ));
                }
              }
              if (unitType && !unitType.category) {
                const dosageForm = dosageForms.find(df => df.key === caseDosageForm.dosageFormType);
                if (unitType.key !== dosageForm.unitType2) {
                  errors.push(caseLogicMsg(
                    'Dosage unit not compatible with concentration', node.id, { errorId: 'dosageUnit', medicationId, checkKey }
                  ));
                }
              }
            }
          }

          // Routes
          if (!caseDosageForm.enabledRoutes.find(route => route.key !== 'IV_IO_INFUSION')) {
            errors.push(caseLogicMsg('Meidcation does not have any routes that can be administered', node.id, { errorId:'routeNoneValid', medicationId, checkKey }));
          }
          (check.routes || []).forEach((routeId) => {
            const route = caseDosageForm.enabledRoutes.find(r => r.key === routeId);
            if (!route) {
              const label = get(defaultRoutes.find(r => r.key === routeId), 'name.en', routeId);
              errors.push(dosageMsg(`Route "${label}" is used in case logic but could not be found`, caseDosageForm.id, { routeId }));
              errors.push(caseLogicMsg(`Route "${label}" could not be found`, node.id, { errorId: 'routeDisabled', medicationId, routeId, checkKey }));
            }
          });

          // Push location
          if (check.ivPushLocation === 'MEDICATION') {
            for (const locationMedId of check.ivPushLocationMedications || []) {
              const dosageForm = caseDosageFormsByMedicationId[locationMedId];
              if (!dosageForm) {
                errors.push(caseLogicMsg('Location medication not enabled', node.id, { errorId: 'locationMed', checkKey }));
              }
            }
          }
          if (check.ivPushLocation === 'FLUID') {
            for (const fluidKey of check.ivPushLocationFluids || []) {
              const fluid = caseConf.fluids.find(f => f.key === fluidKey)
              if (!fluid) {
                errors.push(caseLogicMsg('Location fluid not enabled', node.id, { errorId: 'locationFluid', checkKey }));
              }
            }
          }
        }
      }
      if (check.type === 'MEDICATION_GROUP') {
        if (check.dosage && (check.dosage.min || check.dosage.max) && !check.unit) {
          errors.push(caseLogicMsg('Dosage unit is mandatory', node.id, { errorId: 'unitDisabled', checkKey }));
        }
      }
    });
  });


  return {
    errors,
    warnings,
  };
}

export default validateMedicationNodes;
